import React, { useMemo } from "react";
import dynamicTextData from "../../../assets/dynamicText.json";
import {
  pluralizeRoomType,
  addDefiniteArticle,
  formatNumber,
} from "../../../utils/RoomtypeHelper";
import useAdStatistics from "../utils/hooks/useAdStatistics";

// Static data extracted to JSON for maintainability
const neighborhoods = dynamicTextData.dynamicText.neighborhoods;
const defaultMunicipalities =
  dynamicTextData.dynamicText.defaults.defaultMunicipalities;

// ============================================================================
// HELPER FUNCTIONS - Each handles one specific piece of text generation
// ============================================================================

/**
 * Builds room type text with proper pluralization
 * @param {Array} selectedRoomTypes - Array of selected room type filters
 * @returns {string} Pluralized room type text (e.g., "kontorslokaler" or "lokaler")
 */
const buildRoomTypeText = (selectedRoomTypes) => {
  if (selectedRoomTypes.length === 1) {
    return pluralizeRoomType(selectedRoomTypes[0]);
  } else if (selectedRoomTypes.length > 1) {
    return "lokaler";
  } else {
    return "lokaler";
  }
};

/**
 * Builds municipality text
 * @param {Array} selectedMunicipality - Array of selected municipality filters
 * @returns {string} Capitalized municipality name or default list
 */
const buildMunicipalityText = (selectedMunicipality) => {
  if (selectedMunicipality.length === 1) {
    const currentMunicipality = selectedMunicipality[0];
    return (
      currentMunicipality.charAt(0).toUpperCase() + currentMunicipality.slice(1)
    );
  } else {
    return defaultMunicipalities;
  }
};

/**
 * Builds neighborhood text for a single municipality
 * @param {Array} selectedMunicipality - Array of selected municipality filters
 * @returns {string} Neighborhood text or empty string
 */
const buildNeighborhoodText = (selectedMunicipality) => {
  if (selectedMunicipality.length === 1) {
    const currentMunicipality = selectedMunicipality[0];
    if (neighborhoods[currentMunicipality]) {
      return ` kommun med omnejd i stadsdelar som ${neighborhoods[currentMunicipality]}`;
    }
  }
  return "";
};

/**
 * Builds expansion text based on filter selections
 * @param {Array} selectedMunicipality - Array of selected municipality filters
 * @param {Array} selectedRoomTypes - Array of selected room type filters
 * @returns {string} Expansion text or empty string
 */
const buildExpansionText = (selectedMunicipality, selectedRoomTypes) => {
  if (
    selectedMunicipality.length > 1 ||
    (selectedRoomTypes.length >= 1 && selectedMunicipality.length !== 1)
  ) {
    return " och växer i övriga Sverige";
  }
  return "";
};

/**
 * Builds location phrase from municipality, neighborhood, and expansion text
 * @param {Array} selectedMunicipality - Array of selected municipality filters
 * @param {string} municipalityText - Formatted municipality text
 * @param {string} neighborhoodText - Formatted neighborhood text
 * @param {string} expansionText - Expansion text
 * @returns {string} Complete location phrase (e.g., "i Stockholm kommun med...")
 */
const buildLocationPhrase = (
  selectedMunicipality,
  municipalityText,
  neighborhoodText,
  expansionText
) => {
  if (selectedMunicipality.length) {
    return `i ${municipalityText}${neighborhoodText}${expansionText}`;
  } else {
    return `i ${municipalityText}${expansionText}`;
  }
};

/**
 * Assembles the final text for default case (no filters)
 * @returns {string} Default Vakansa description
 */
const buildDefaultText = () => {
  return `Vakansa har ett brett utbud av lokaler som kan hyras i andrahand för alla möjliga tillfällen. Antingen per timme, dag, månad och i flera år. Vi har ett stort utbud av lokaler i ${defaultMunicipalities} och växer i övriga Sverige.`;
};

/**
 * Assembles the final text for filtered case
 * @param {string} roomTypeText - Formatted room type text
 * @param {string} locationPhrase - Formatted location phrase
 * @returns {string} Complete filtered text
 */
const buildFilteredText = (roomTypeText, locationPhrase) => {
  return `Här på Vakansa kan du hitta lediga ${roomTypeText} att hyra ${locationPhrase}. ${
    roomTypeText.charAt(0).toUpperCase() + roomTypeText.slice(1)
  } som kan hyras per timme, dag, månad eller år!`;
};

/**
 * Builds price and area statistics text from ad data
 * @param {Object} statistics - Statistics object from useAdStatistics
 * @param {string} roomTypeText - Room type text for context
 * @returns {string} Price and area statistics text (e.g., "Lokalerna har prisspannet 250 till 10 000 kr...")
 */
const buildPriceAreaText = (statistics, roomTypeText) => {
  if (!statistics) return "";

  const { minPrice, maxPrice, minArea, maxArea } = statistics;

  // If no valid price or area data, return empty string
  if (
    minPrice === Infinity ||
    maxPrice === -Infinity ||
    minArea === Infinity ||
    maxArea === -Infinity ||
    minPrice === null ||
    maxPrice === null ||
    minArea === null ||
    maxArea === null ||
    minPrice === undefined ||
    maxPrice === undefined ||
    minArea === undefined ||
    maxArea === undefined
  ) {
    return "";
  }

  // Add definite article to room type (e.g., "kontorslokaler" -> "kontorslokalerna")
  const roomTypeWithArticle = addDefiniteArticle(roomTypeText);

  // Capitalize first letter for sentence start
  const capitalizedRoomType =
    roomTypeWithArticle.charAt(0).toUpperCase() + roomTypeWithArticle.slice(1);

  // Format numbers with Swedish thousand separators
  const formattedMinPrice = formatNumber(minPrice);
  const formattedMaxPrice = formatNumber(maxPrice);
  const formattedMinArea = formatNumber(minArea);
  const formattedMaxArea = formatNumber(maxArea);

  return ` ${capitalizedRoomType} har prisspannet ${formattedMinPrice} till ${formattedMaxPrice} kr och ytorna ligger på mellan ${formattedMinArea} till ${formattedMaxArea} m².`;
};

/**
 * Builds moms/VAT statistics text from ad data
 * @param {Object} statistics - Statistics object from useAdStatistics
 * @param {string} roomTypeText - Room type text for context
 * @param {string} municipalityText - Municipality text for context (if single)
 * @param {Array} selectedMunicipality - Selected municipality array
 * @returns {string} Moms statistics text (e.g., "Vi har i dagsläget 25 omomsade lokaler...")
 */
const buildMomsText = (
  statistics,
  roomTypeText,
  municipalityText,
  selectedMunicipality
) => {
  if (!statistics) return "";

  const { adsWithMoms, adsWithoutMoms } = statistics;

  // If both are 0, return empty string
  if (adsWithMoms === 0 && adsWithoutMoms === 0) return "";

  // Build parts
  const parts = [];

  if (adsWithoutMoms > 0) {
    parts.push(`${adsWithoutMoms} st omomsade ${roomTypeText}`);
  }

  if (adsWithMoms > 0) {
    parts.push(`${adsWithMoms} st momsade ${roomTypeText}`);
  }

  // Join with "och" if both exist
  const momsText = parts.join(" och ");

  // Add location if single municipality
  const locationText =
    selectedMunicipality.length === 1 ? ` i ${municipalityText}` : "";

  return ` Vi har i dagsläget ${momsText}${locationText}.`;
};

// ============================================================================
// MAIN GENERATOR FUNCTION
// ============================================================================

// Export generator function for direct use when needed
export const generateDynamicText = (
  selectedMunicipality,
  selectedRoomTypes,
  statistics = null
) => {
  // Build individual text pieces using helper functions
  const roomTypeText = buildRoomTypeText(selectedRoomTypes);
  const municipalityText = buildMunicipalityText(selectedMunicipality);
  const neighborhoodText = buildNeighborhoodText(selectedMunicipality);
  const expansionText = buildExpansionText(
    selectedMunicipality,
    selectedRoomTypes
  );

  // Check if filters are applied
  const hasFilters = selectedMunicipality.length || selectedRoomTypes.length;

  if (!hasFilters) {
    const priceAreaText = buildPriceAreaText(statistics, "lokaler");
    const momsText = buildMomsText(
      statistics,
      "lokaler",
      municipalityText,
      selectedMunicipality
    );
    return buildDefaultText() + priceAreaText + momsText;
  }

  // Build location phrase and assemble final text
  const locationPhrase = buildLocationPhrase(
    selectedMunicipality,
    municipalityText,
    neighborhoodText,
    expansionText
  );

  const priceAreaText = buildPriceAreaText(statistics, roomTypeText);
  const momsText = buildMomsText(
    statistics,
    roomTypeText,
    municipalityText,
    selectedMunicipality
  );

  return (
    buildFilteredText(roomTypeText, locationPhrase) + priceAreaText + momsText
  );
};

// Custom hook for reusing the memoized dynamic text
export const useDynamicText = (
  selectedMunicipality,
  selectedRoomTypes,
  statistics = null
) => {
  return useMemo(
    () =>
      generateDynamicText(selectedMunicipality, selectedRoomTypes, statistics),
    [selectedMunicipality, selectedRoomTypes, statistics]
  );
};

const DynamicText = ({ selectedMunicipality, selectedRoomTypes, allAds }) => {
  const { minPrice, maxPrice, minArea, maxArea, adsWithMoms, adsWithoutMoms } =
    useAdStatistics(allAds);

  const statistics = {
    minPrice,
    maxPrice,
    minArea,
    maxArea,
    adsWithMoms,
    adsWithoutMoms,
  };

  const dynamicText = useDynamicText(
    selectedMunicipality,
    selectedRoomTypes,
    statistics
  );

  // console.log("Statistics:", statistics);

  return <p>{dynamicText}</p>;
};

export default DynamicText;
